import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  apiErrorText,
  useCreateAutoPtoSqFromRdMutation,
  useDecidePtoRdGapFindingMutation,
  useEstimatePtoRdGapsAiMutation,
  useGetPtoRdGapsConfigQuery,
  useGetPtoRdGapsQuery,
  useRunPtoRdGapsAiMutation,
  useRunPtoRdGapsMutation,
  useSavePtoRdGapRuleMutation,
  type PtoRdGapAiSelection,
  type PtoRdGapFinding,
  type PtoRdGapRun,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import type { SheetTarget } from '../rdSheet/protocol';
import * as logic from './logic';

// Вкладка «Анализ комплектности РД» (№7, docs/pto-rd-gaps-spec.md): чего,
// возможно, не хватает в комплекте — по нормам ГЭСН (оценка «норма × объём»,
// не для заказа), по типовому комплекту и по сверке ВОР↔СП; по кнопке — ИИ
// (предложения с цитатой из РД или шифром нормы, проверены Flash и
// программой). Решает инженер; принятое — только справочный лист
// «Дополнительно (нет в РД)» и запрос SQ.

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

const Muted = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 13px;
`;

const Warn = styled.div`
  font-size: 13px;
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.colors.status.warning}55;
  background: ${({ theme }) => theme.colors.status.warning}14;
`;

const Pill = styled.span<{ $tone?: 'warn' | 'ok' | 'muted' | 'info' }>`
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  padding: 1px 8px;
  border-radius: ${({ theme }) => theme.radius.pill};
  white-space: nowrap;
  border: 1px solid
    ${({ $tone, theme }) =>
      $tone === 'warn'
        ? theme.colors.status.danger
        : $tone === 'ok'
          ? theme.colors.status.success
          : $tone === 'info'
            ? theme.colors.status.info
            : theme.colors.border};
  color: ${({ $tone, theme }) =>
    $tone === 'warn'
      ? theme.colors.status.danger
      : $tone === 'ok'
        ? theme.colors.status.success
        : $tone === 'info'
          ? theme.colors.status.info
          : theme.colors.text.secondary};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    text-align: left;
    padding: ${({ theme }) => theme.spacing(1.5)} ${({ theme }) => theme.spacing(2)};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    vertical-align: top;
  }

  th {
    color: ${({ theme }) => theme.colors.text.secondary};
    font-weight: 600;
    white-space: nowrap;
  }

  td.num {
    text-align: right;
    white-space: nowrap;
  }
`;

const Group = styled.section`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  overflow: hidden;
`;

const GroupHead = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  border: none;
  cursor: pointer;
  font: inherit;
  font-weight: 600;
  text-align: left;
`;

const Card = styled.div<{ $muted: boolean }>`
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(3)};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  opacity: ${({ $muted }) => ($muted ? 0.65 : 1)};
`;

const CardHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

const Name = styled.div`
  font-weight: 600;
`;

const Estimate = styled.div`
  font-size: 13px;
  margin-top: 2px;
`;

const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(1.5)};
  flex-wrap: wrap;
  align-items: center;
`;

const Details = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Note = styled.input`
  font: inherit;
  font-size: 13px;
  padding: ${({ theme }) => theme.spacing(1)} ${({ theme }) => theme.spacing(2)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  min-width: 260px;
`;

const Toggle = styled.div`
  display: inline-flex;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  overflow: hidden;
`;

const ToggleBtn = styled.button<{ $on: boolean }>`
  font: inherit;
  font-size: 13px;
  padding: ${({ theme }) => theme.spacing(1)} ${({ theme }) => theme.spacing(3)};
  border: none;
  cursor: pointer;
  background: ${({ $on, theme }) => ($on ? theme.colors.primarySoft : 'transparent')};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Small = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const AiBox = styled.section`
  border: 1px solid ${({ theme }) => theme.colors.status.info}66;
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(3)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const PickList = styled.div`
  max-height: 280px;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  padding: ${({ theme }) => theme.spacing(1)} ${({ theme }) => theme.spacing(2)};
`;

const PickRow = styled.label`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 13px;
  padding: 2px 0;
`;

const PickHead = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: ${({ theme }) => theme.spacing(1.5)};
`;

const QuoteLine = styled.div`
  font-size: 13px;
  margin-top: 4px;
  font-style: italic;
`;

const LinkBtn = styled.button`
  font: inherit;
  font-size: 12px;
  font-style: normal;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.status.info};
  text-decoration: underline;
`;

/** «Источник» цитаты без страницы — без ссылки, текст листа в подсказке. */
const SourceText = styled.span`
  font-size: 12px;
  font-style: normal;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: help;
`;

interface Props {
  projectId: string;
  revisionId: string;
  documentId?: string;
  /** Шифр комплекта — в шапку окна листа РД. */
  docCode: string | null;
  canWrite: boolean;
  canSaveRules: boolean;
  mode: 'off' | 'dry' | 'on';
  /** Строка ВОР со значка «⚠ N» (record_uid) — показать только её находки. */
  rowUid: string | null;
  onClearRow: () => void;
  /** Открыть лист РД в окне исходного листа (одно окно на экран, выпуск 1б). */
  onOpenSheet: (target: SheetTarget) => void;
}

function fmtTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' });
}

function seconds(run: { started_at: string | null; finished_at: string | null }): string | null {
  if (!run.started_at || !run.finished_at) return null;
  const ms = Date.parse(run.finished_at) - Date.parse(run.started_at);
  return Number.isFinite(ms)
    ? `${(ms / 1000).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} с`
    : null;
}

function RowsTable({ rows, withNorm }: { rows: logic.EvidenceRow[]; withNorm: boolean }) {
  if (rows.length === 0) return null;
  return (
    <Table>
      <thead>
        <tr>
          <th>Стр.</th>
          <th>Строка РД</th>
          <th className="num">Кол-во</th>
          <th>Ед.</th>
          <th>Система</th>
          {withNorm && <th>Норма ГЭСН и расчёт</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={`${r.id ?? r.uid ?? 'row'}-${i}`}>
            <td>{r.page ?? '—'}</td>
            <td>{r.name}</td>
            <td className="num">{logic.fmtNum(r.qty)}</td>
            <td>{r.unit ?? '—'}</td>
            <td>{r.system ?? '—'}</td>
            {withNorm && (
              <td>
                {r.norm ?? '—'}
                {r.formula && <Small>{r.formula}</Small>}
                {r.refineNote && <Small>уточнение строки нормы: {r.refineNote}</Small>}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

/** Цитата ИИ: текст и «Источник: стр. PDF N» — открывает лист РД на этой
 *  странице в окне исходного листа (выпуск 1б, №9; цитата — в шапке окна).
 *  Текст листа вокруг цитаты, сверенный программой, — подсказкой к
 *  «Источнику». Страницы нет — «Источник» без ссылки. */
function AiQuoteBlock({ quote, onOpen }: { quote: logic.AiQuote; onOpen: (() => void) | null }) {
  const hint = logic.quoteHint(quote);
  const label = `Источник: ${logic.quotePlace(quote) || 'стр. —'}`;
  return (
    <QuoteLine>
      «{quote.text}»{' '}
      {onOpen ? (
        <LinkBtn type="button" title={hint} onClick={onOpen}>
          {label}
        </LinkBtn>
      ) : (
        <SourceText title={hint}>{label}</SourceText>
      )}
    </QuoteLine>
  );
}

function FindingDetails({ f }: { f: PtoRdGapFinding }) {
  const ev = logic.evidenceOf(f);
  const norms = logic.normRefs(f);
  const ai = logic.aiInfo(f);
  return (
    <Details>
      {ai && ai.kind === 'proposal' && (
        <>
          {ai.why && <Small>Почему: {ai.why}</Small>}
          {ai.verdictReason && <Small>Проверка Flash: подтверждено — {ai.verdictReason}</Small>}
          {ai.groupLabel && <Small>Спрошено по группе: {ai.groupLabel}</Small>}
        </>
      )}
      {norms.length > 0 && (
        <Table>
          <thead>
            <tr>
              <th>Норма</th>
              <th>Наименование нормы</th>
              <th className="num">Расход на ед.</th>
              <th className="num">Строк</th>
              <th className="num">Оценка</th>
            </tr>
          </thead>
          <tbody>
            {norms.map((n) => (
              <tr key={n.code}>
                <td>{n.code}</td>
                <td>
                  {n.name}
                  {n.content.length > 0 && <Small>состав работ: {n.content.join(' ')}</Small>}
                </td>
                <td className="num">
                  {n.perUnit === null
                    ? '—'
                    : `${logic.fmtNum(n.perUnit)} ${f.unit ?? ''} на ${n.unit ?? 'ед.'}`}
                </td>
                <td className="num">{n.rows || '—'}</td>
                <td className="num">
                  {n.estimate === null ? '—' : `${logic.fmtNum(n.estimate)} ${f.unit ?? ''}`}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {ev.kitRule && (
        <Small>
          Типовой комплект: «{ev.kitRule.workLabel}» → {ev.kitRule.material}
          {ev.kitRowsBeyondNorm.length > 0 &&
            ` · строк вида работ без этого материала в норме: ${ev.kitRowsBeyondNorm.length}`}
        </Small>
      )}
      {ev.reconcile && (
        <>
          <Small>
            ВОР: {logic.fmtNum(ev.reconcile.vor.sum)} {ev.reconcile.vor.unit ?? ''} · СП:{' '}
            {logic.fmtNum(ev.reconcile.sp.sum)} {ev.reconcile.sp.unit ?? ''}
            {ev.reconcile.deltaPct !== null &&
              ` · разница ${logic.fmtNum(ev.reconcile.deltaPct, 1)} %`}
          </Small>
          <Small>Строки ВОР</Small>
          <RowsTable rows={[...ev.reconcile.vor.rows]} withNorm={false} />
          <Small>Строки СП</Small>
          <RowsTable rows={[...ev.reconcile.sp.rows]} withNorm={false} />
          {(ev.reconcile.vor.noQty.length > 0 || ev.reconcile.sp.noQty.length > 0) && (
            <Small>
              Без количества (сверить по PDF): ВОР — {ev.reconcile.vor.noQty.length}, СП —{' '}
              {ev.reconcile.sp.noQty.length}
            </Small>
          )}
        </>
      )}
      {ai && ai.kind === 'proposal' && ev.rows.length > 0 && <Small>Строки ВОР, к которым относится:</Small>}
      {!ev.reconcile && <RowsTable rows={ev.rows} withNorm={logic.hasNormEstimate(f)} />}
      {ev.rowsWithoutQty.length > 0 && (
        <>
          <Small>Строки ВОР без количества — в оценку не вошли (сверить по PDF):</Small>
          <RowsTable rows={ev.rowsWithoutQty} withNorm={false} />
        </>
      )}
      {ev.kitRowsBeyondNorm.length > 0 && (
        <>
          <Small>Строки вида работ, у нормы которых этого материала нет:</Small>
          <RowsTable rows={ev.kitRowsBeyondNorm} withNorm={false} />
        </>
      )}
      {(ev.presenceNotes.length > 0 || ev.searchedRows !== null) && (
        <Small>
          Наличие в СП
          {ev.searchedRows !== null && ` (искали среди ${ev.searchedRows} строк изделий)`}:{' '}
          {logic.presenceLabel(f.presence) ?? '—'}
          {ev.presenceNotes.length > 0 && ` · ${ev.presenceNotes.join(' · ')}`}
        </Small>
      )}
      {ev.presenceHits.length > 0 && (
        <Small>
          Похожие изделия СП:{' '}
          {ev.presenceHits.map((h) => `«${h.name}» (стр. ${h.page ?? '—'})`).join('; ')}
        </Small>
      )}
      {ev.presenceOther.length > 0 && (
        <Small>
          Найдено, но другое: {ev.presenceOther.map((o) => `«${o.row.name}» — ${o.why}`).join('; ')}
        </Small>
      )}
      {f.note && <Small>Примечание инженера: {f.note}</Small>}
    </Details>
  );
}

function FindingCard({
  f,
  revisionId,
  docCode,
  canWrite,
  canSaveRules,
  onOpenSheet,
  estimate,
}: {
  f: PtoRdGapFinding;
  revisionId: string;
  docCode: string | null;
  canWrite: boolean;
  canSaveRules: boolean;
  onOpenSheet: (target: SheetTarget) => void;
  /** Оценка в разрезе системы (вид «По системам»). */
  estimate?: number | null;
}) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [decide, { isLoading: deciding }] = useDecidePtoRdGapFindingMutation();
  const [saveRule, { isLoading: saving }] = useSavePtoRdGapRuleMutation();
  const hidden = f.visibility !== 'show';
  const presence = logic.presenceLabel(f.presence);
  const qty = estimate !== undefined ? estimate : f.qty_estimate;
  const ai = logic.aiInfo(f);
  // Лист цитаты — в окне исходного листа РД (одно окно на все клики).
  const quoteTarget = ai?.quote ? logic.quoteSheetTarget(revisionId, ai.quote, docCode) : null;

  async function onDecide(decision: 'accept' | 'reject' | 'reset') {
    setMsg(null);
    try {
      await decide({
        findingId: f.id,
        revisionId,
        decision,
        note: note.trim() || undefined,
      }).unwrap();
    } catch (e) {
      setMsg(apiErrorText(e, 'Не удалось сохранить решение.'));
    }
  }

  async function onSaveRule() {
    setMsg(null);
    try {
      const res = await saveRule({ findingId: f.id, revisionId }).unwrap();
      setMsg(
        res.created
          ? `Сохранено в типовой комплект: «${res.rule.work_label}» → ${res.rule.material_name}.`
          : 'Такое правило типового комплекта уже есть.',
      );
    } catch (e) {
      setMsg(apiErrorText(e, 'Не удалось сохранить правило.'));
    }
  }

  return (
    <Card $muted={hidden || f.status === 'rejected'}>
      <CardHead>
        <div style={{ flex: '1 1 320px' }}>
          <Name>{f.name}</Name>
          <Actions style={{ marginTop: 4 }}>
            <Pill $tone="info">{logic.LAYER_LABEL[f.layer] ?? f.layer}</Pill>
            {ai && <Pill $tone="info">{ai.label}</Pill>}
            {f.layer !== 'norm' && !(ai && ai.kind === 'norm_choice') && (
              <Pill>{logic.KIND_LABEL[f.kind] ?? f.kind}</Pill>
            )}
            {presence && <Pill $tone="warn">{presence}</Pill>}
            {hidden && <Pill>{logic.visibilityNote(f.visibility)}</Pill>}
            {f.status !== 'proposed' && (
              <Pill $tone={f.status === 'accepted' ? 'ok' : 'muted'}>
                {logic.STATUS_LABEL[f.status] ?? f.status}
              </Pill>
            )}
            {f.sq_request_id && <Pill $tone="ok">в запросе SQ</Pill>}
          </Actions>
          {qty !== null && qty !== undefined && (
            <Estimate>
              ≈ {logic.fmtNum(qty)} {f.unit ?? ''} <Small as="span">({f.qty_note ?? ''})</Small>
            </Estimate>
          )}
          {qty === null && f.qty_note && <Small>{f.qty_note}</Small>}
          {ai?.quote && (
            <AiQuoteBlock
              quote={ai.quote}
              onOpen={quoteTarget ? () => onOpenSheet(quoteTarget) : null}
            />
          )}
        </div>
        <Actions>
          <Button $variant="ghost" $size="sm" onClick={() => setOpen((v) => !v)}>
            {open ? 'Скрыть' : 'Основание'}
          </Button>
          {canWrite && f.status !== 'accepted' && (
            <Button
              $variant="secondary"
              $size="sm"
              disabled={deciding}
              onClick={() => void onDecide('accept')}
            >
              Принять
            </Button>
          )}
          {canWrite && f.status !== 'rejected' && (
            <Button
              $variant="ghost"
              $size="sm"
              disabled={deciding}
              onClick={() => void onDecide('reject')}
            >
              Отклонить
            </Button>
          )}
          {canWrite && f.status !== 'proposed' && (
            <Button
              $variant="ghost"
              $size="sm"
              disabled={deciding}
              onClick={() => void onDecide('reset')}
            >
              Снять решение
            </Button>
          )}
          {canSaveRules && f.status === 'accepted' && f.layer !== 'reconcile' && (
            <Button
              $variant="ghost"
              $size="sm"
              disabled={saving}
              onClick={() => void onSaveRule()}
              title="Правило вида работ без количеств: при следующих проверках материал будет предлагаться по этому виду работ на всех проектах"
            >
              Сохранить как типовой комплект
            </Button>
          )}
        </Actions>
      </CardHead>
      {msg && <Small style={{ marginTop: 6 }}>{msg}</Small>}
      {open && (
        <>
          <FindingDetails f={f} />
          {canWrite && (
            <Actions style={{ marginTop: 8 }}>
              <Note
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Примечание к решению (необязательно)"
                maxLength={2000}
              />
            </Actions>
          )}
        </>
      )}
    </Card>
  );
}

/** «Спросить ИИ»: выбор групп с ценой → оценка сервера → подтверждение. */
function AiAskPanel({
  revisionId,
  onStarted,
  onClose,
}: {
  revisionId: string;
  onStarted: (note: string | null) => void;
  onClose: () => void;
}) {
  const [estimate, { data, isLoading: estimating }] = useEstimatePtoRdGapsAiMutation();
  const [runAi, { isLoading: starting }] = useRunPtoRdGapsAiMutation();
  const [selected, setSelected] = useState<Set<string>>(() => new Set(['set']));
  const [normChoice, setNormChoice] = useState(false);
  const [quote, setQuote] = useState<PtoRdGapAiSelection | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [planError, setPlanError] = useState<string | null>(null);

  useEffect(() => {
    // План — без выбора: модель не вызывается, только цены групп.
    estimate({ revisionId, groups: [], norm_choice: false })
      .unwrap()
      .catch((e) => setPlanError(apiErrorText(e, 'Не удалось получить оценку.')));
  }, [revisionId, estimate]);

  const plan = data?.plan ?? null;
  const local = plan ? logic.selectionEstimate(plan, selected, normChoice) : null;
  const sections = useMemo(() => {
    const g = plan?.groups ?? [];
    return [
      { title: 'Комплект', items: g.filter((x) => x.kind === 'set') },
      { title: 'Виды работ', items: g.filter((x) => x.kind === 'work') },
      { title: 'Системы', items: g.filter((x) => x.kind === 'system') },
    ].filter((s) => s.items.length > 0);
  }, [plan]);

  function toggle(key: string) {
    setQuote(null);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function onEstimate() {
    setMsg(null);
    setQuote(null);
    try {
      const res = await estimate({
        revisionId,
        groups: [...selected],
        norm_choice: normChoice,
      }).unwrap();
      if (res.selection.allowed) setQuote(res.selection);
      else setMsg(res.selection.reason);
    } catch (e) {
      setMsg(apiErrorText(e, 'Не удалось оценить стоимость.'));
    }
  }

  async function onConfirm() {
    if (!quote) return;
    setMsg(null);
    try {
      const res = await runAi({
        revisionId,
        groups: quote.request.groups,
        norm_choice: quote.request.norm_choice,
        confirm_usd_micro: quote.estimate.usd_micro,
      }).unwrap();
      onStarted(res.created ? null : 'Прогон ИИ уже идёт — дождитесь результата.');
    } catch (e) {
      // 409 — цена изменилась (изменились позиции РД): оценить заново.
      setQuote(null);
      setMsg(apiErrorText(e, 'Не удалось запустить ИИ.'));
    }
  }

  return (
    <AiBox>
      <Small>
        ИИ (DeepSeek) предложит, чего может не хватать в ВОР и СП, — с дословной цитатой из
        общих указаний РД (программа сверяет её с текстом листа) или шифром нормы ГЭСН, без
        количеств. Каждое предложение проверяет вторая модель (Flash). Только по кнопке, лимит —{' '}
        {plan ? logic.fmtUsd(plan.max_usd_micro) : '…'} на прогон.
      </Small>
      {planError && <Warn>{planError}</Warn>}
      {!plan && !planError && <Muted>Считаем оценку…</Muted>}
      {plan && (
        <>
          <Small>
            Во входе модели: общие указания и примечания (стр.{' '}
            {plan.notes_pages.length > 0 ? plan.notes_pages.join(', ') : '—'}
            {plan.notes_truncated ? ', часть не вошла' : ''}), строк ВОР без повторов —{' '}
            {plan.vor_items}, изделий СП — {plan.sp_items}.
          </Small>
          <PickList>
            {sections.map((s) => (
              <div key={s.title}>
                <PickHead>{s.title}</PickHead>
                {s.items.map((g) => (
                  <PickRow key={g.key}>
                    <input
                      type="checkbox"
                      checked={selected.has(g.key)}
                      onChange={() => toggle(g.key)}
                    />
                    <span style={{ flex: 1 }}>
                      {g.label}
                      <Small as="span"> · строк ВОР {g.rows}</Small>
                    </span>
                    <Small as="span">
                      {g.unchanged ? 'ответ ИИ актуален' : `≈${logic.fmtUsd(g.estimate.usd_micro)}`}
                    </Small>
                  </PickRow>
                ))}
              </div>
            ))}
          </PickList>
          <PickRow>
            <input
              type="checkbox"
              checked={normChoice}
              disabled={plan.norm_choice.unique === 0}
              onChange={(e) => {
                setQuote(null);
                setNormChoice(e.target.checked);
              }}
            />
            <span style={{ flex: 1 }}>
              Подобрать нормы ГЭСН моделью для строк ВОР без нормы (сходство ниже 0,85): строк{' '}
              {plan.norm_choice.rows}, без повторов {plan.norm_choice.unique} — модель выбирает из
              кандидатов, программа проверяет выбор, «норма × объём» считает программа
            </span>
            <Small as="span">
              {plan.norm_choice.unchanged
                ? 'ответ ИИ актуален'
                : `≈${logic.fmtUsd(plan.norm_choice.estimate.usd_micro)}`}
            </Small>
          </PickRow>
          <Bar>
            {local && (
              <Muted>
                Итого ≈{logic.fmtUsd(local.usdMicro)} · вызовов модели {local.calls} (лимит{' '}
                {logic.fmtUsd(plan.max_usd_micro)} и {plan.max_calls} вызовов)
              </Muted>
            )}
            <Button
              $variant="primary"
              $size="sm"
              disabled={!local || local.asked === 0 || estimating || starting}
              onClick={() => void onEstimate()}
            >
              {estimating ? 'Оценка…' : `Спросить ИИ — ≈${logic.fmtUsd(local?.usdMicro ?? 0)}`}
            </Button>
            <Button $variant="ghost" $size="sm" onClick={onClose}>
              Закрыть
            </Button>
          </Bar>
          {quote && (
            <Warn>
              Оценка: ≈{logic.fmtUsd(quote.estimate.usd_micro)} — вызовов модели{' '}
              {quote.estimate.calls}, ≈{logic.fmtNum(quote.estimate.input_tokens, 0)} токенов входа
              (без скидки кэша). Запустить?{' '}
              <Actions style={{ marginTop: 6 }}>
                <Button
                  $variant="primary"
                  $size="sm"
                  disabled={starting}
                  onClick={() => void onConfirm()}
                >
                  {starting ? 'Запуск…' : 'Подтвердить и спросить'}
                </Button>
                <Button $variant="ghost" $size="sm" onClick={() => setQuote(null)}>
                  Отмена
                </Button>
              </Actions>
            </Warn>
          )}
        </>
      )}
      {msg && <Warn>{msg}</Warn>}
    </AiBox>
  );
}

/** Итог последнего прогона ИИ: когда, сколько стоил, что отброшено и почему. */
function AiRunLine({ run }: { run: PtoRdGapRun }) {
  const [open, setOpen] = useState(false);
  const stats = logic.aiRunStats(run.summary);
  return (
    <>
      <Muted>
        ИИ: {fmtTime(run.finished_at)} · ≈{logic.fmtUsd(run.cost_usd_micro)} (оценка ≈
        {logic.fmtUsd(run.est_cost_usd_micro)}) · вызовов {run.llm_calls}
        {run.status === 'partial' ? ' · выполнен частично' : ''} · предложений {stats.proposals},
        прошли проверку {stats.kept}, отброшено программой {stats.droppedByCode}, Flash{' '}
        {stats.droppedByFlash}
        {stats.normsPicked > 0 ? ` · норм выбрано ${stats.normsPicked}` : ''}
        {stats.dropped.length > 0 && (
          <>
            {' '}
            <LinkBtn type="button" onClick={() => setOpen((v) => !v)}>
              {open ? 'Скрыть отброшенное' : 'Что отброшено и почему'}
            </LinkBtn>
          </>
        )}
      </Muted>
      {open && stats.dropped.length > 0 && (
        <Table>
          <thead>
            <tr>
              <th>Группа</th>
              <th>Предложение</th>
              <th>Кто отбросил</th>
              <th>Причина</th>
            </tr>
          </thead>
          <tbody>
            {stats.dropped.map((d, i) => (
              <tr key={`${d.group}-${d.name}-${i}`}>
                <td>{d.group}</td>
                <td>{d.name}</td>
                <td>{d.stage === 'flash' ? 'Flash' : 'программа'}</td>
                <td>{d.reason}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );
}

export function RdGapsPanel({
  projectId,
  revisionId,
  documentId,
  docCode,
  canWrite,
  canSaveRules,
  mode,
  rowUid,
  onClearRow,
  onOpenSheet,
}: Props) {
  const [polling, setPolling] = useState(false);
  const { data, isLoading, isError, error } = useGetPtoRdGapsQuery(revisionId, {
    skip: !revisionId,
    pollingInterval: polling ? 2000 : 0,
  });
  // Настройка вкладки (кэш общий с экраном позиций): кнопка ИИ.
  const { data: cfg } = useGetPtoRdGapsConfigQuery(projectId, { skip: !projectId });
  const pending = data?.pending ?? null;
  const aiPending = data?.ai?.pending ?? null;
  const pendingNow = Boolean(pending) || Boolean(aiPending);
  useEffect(() => {
    // Опрос — только пока идёт прогон (без ИИ или ИИ).
    setPolling(pendingNow);
  }, [pendingNow]);
  const [runGaps, { isLoading: starting }] = useRunPtoRdGapsMutation();
  const [createSq, { isLoading: creatingSq }] = useCreateAutoPtoSqFromRdMutation();
  const [note, setNote] = useState<string | null>(null);
  const [view, setView] = useState<'work' | 'system'>('work');
  const [showHidden, setShowHidden] = useState(false);
  const [status, setStatus] = useState<logic.FindingFilter['status']>('all');
  const [askAi, setAskAi] = useState(false);

  const run = data?.run ?? null;
  const aiRun = data?.ai?.run ?? null;
  const aiEnabled = cfg?.ai_enabled === true && data?.ai?.available === true;
  const findings = data?.findings ?? [];
  const filtered = useMemo(
    () => logic.filterFindings(findings, { showHidden, status, rowUid }),
    [findings, showHidden, status, rowUid],
  );
  const groups = useMemo(() => logic.groupByWorkKind(filtered), [filtered]);
  const systems = useMemo(() => logic.groupBySystem(filtered), [filtered]);
  const totals = useMemo(() => logic.totalsOf(run?.summary), [run]);
  const coverage = logic.coverageText(run?.summary);
  const hiddenCount = findings.filter((f) => f.visibility !== 'show').length;
  const forSq = logic.acceptedNotInSq(findings);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  async function onRun() {
    setNote(null);
    try {
      const res = await runGaps(revisionId).unwrap();
      setPolling(true);
      if (!res.created) setNote('Проверка уже идёт — дождитесь результата.');
    } catch (e) {
      setNote(apiErrorText(e, 'Не удалось запустить проверку.'));
    }
  }

  async function onSq() {
    setNote(null);
    try {
      const res = await createSq({ projectId, document_id: documentId }).unwrap();
      setNote(
        `Сформирован запрос SQ «${res.request.subject}»: находок анализа комплектности — ${
          res.gap_findings ?? 0
        }. Перейдите в «Изменения и SQ».`,
      );
    } catch (e) {
      setNote(apiErrorText(e, 'Не удалось сформировать запрос SQ.'));
    }
  }

  return (
    <Wrap>
      <Bar>
        {canWrite && (
          <Button
            $variant="primary"
            $size="sm"
            disabled={starting || Boolean(pending)}
            onClick={() => void onRun()}
            title="Сопоставить строки ВОР с нормами ГЭСН, проверить наличие материалов норм в СП и сверить ВОР со СП. Без ИИ, бесплатно."
          >
            {pending ? 'Идёт проверка…' : starting ? 'Запуск…' : 'Проверить по нормам'}
          </Button>
        )}
        {canWrite && aiEnabled && (
          <Button
            $variant="secondary"
            $size="sm"
            disabled={Boolean(aiPending)}
            onClick={() => setAskAi((v) => !v)}
            title="ИИ по кнопке: предложения с цитатой из РД или шифром нормы, с оценкой стоимости до запуска"
          >
            {aiPending ? logic.aiProgressText(aiPending) : 'Спросить ИИ…'}
          </Button>
        )}
        {canWrite && forSq > 0 && (
          <Button
            $variant="secondary"
            $size="sm"
            disabled={creatingSq}
            onClick={() => void onSq()}
            title="Принятые находки — разделом «Возможно не учтено в РД» в автозапрос SQ проектировщику"
          >
            {creatingSq ? 'Сбор SQ…' : `В запрос SQ (${forSq})`}
          </Button>
        )}
        {mode === 'dry' && <Pill $tone="info">пилот</Pill>}
        {run && (
          <Muted>
            Проверено {fmtTime(run.finished_at)}
            {seconds(run) ? ` за ${seconds(run)}` : ''}
            {run.norm_edition ? ` · нормы ${run.norm_edition}` : ''}
          </Muted>
        )}
      </Bar>
      {askAi && aiEnabled && !aiPending && (
        <AiAskPanel
          revisionId={revisionId}
          onClose={() => setAskAi(false)}
          onStarted={(n) => {
            setAskAi(false);
            setPolling(true);
            if (n) setNote(n);
          }}
        />
      )}
      {aiRun && <AiRunLine run={aiRun} />}
      {note && <Muted>{note}</Muted>}
      {data?.stale && run && (
        <Warn>
          После проверки изменились позиции РД, нормы или правила типового комплекта — запустите
          «Проверить по нормам» заново. Решения по находкам перенесутся.
        </Warn>
      )}
      {data?.ai?.stale && aiRun && (
        <Warn>
          После прогона ИИ изменились позиции РД — предложения ИИ могли устареть. Новый вопрос
          спросит заново только изменившиеся группы; решения по находкам перенесутся.
        </Warn>
      )}
      {data?.runs?.[0]?.status === 'failed' && (
        <Warn>Последняя проверка не выполнена: {data.runs[0].error_message}</Warn>
      )}
      {data?.ai?.runs?.[0]?.status === 'failed' && (
        <Warn>Последний прогон ИИ не выполнен: {data.ai.runs[0].error_message}</Warn>
      )}

      {isLoading ? (
        <Muted>Загрузка…</Muted>
      ) : isError ? (
        <Muted style={{ color: 'crimson' }}>
          {apiErrorText(error, 'Не удалось загрузить анализ.')}
        </Muted>
      ) : !run && !aiRun ? (
        <Muted>
          Проверки ещё не было.{' '}
          {canWrite
            ? 'Нажмите «Проверить по нормам» — это несколько секунд.'
            : 'Её запускает инженер проекта.'}
        </Muted>
      ) : (
        <>
          <Muted>
            Количества — {data?.qty_note}. Предложения ИИ — без количеств, с цитатой из РД или
            шифром нормы. Находки не становятся позициями РД и не уходят в снабжение: принятое
            попадает на лист «Дополнительно (нет в РД)» ведомости и в запрос SQ.
          </Muted>
          {coverage && <Small>{coverage}</Small>}

          {totals.filter((t) => showHidden || t.visibility === 'show').length > 0 && (
            <Group>
              <GroupHead as="div">Итого по комплекту (оценка по нормам ГЭСН)</GroupHead>
              <Table>
                <thead>
                  <tr>
                    <th>Материал</th>
                    <th className="num">Оценка</th>
                    <th>Ед.</th>
                    <th className="num">Норм</th>
                    <th className="num">Строк ВОР</th>
                    <th>Наличие в СП</th>
                  </tr>
                </thead>
                <tbody>
                  {totals
                    .filter((t) => showHidden || t.visibility === 'show')
                    .map((t) => (
                      <tr key={t.resourceCode}>
                        <td>
                          {t.name}
                          {t.visibility !== 'show' && (
                            <Small>{logic.visibilityNote(t.visibility)}</Small>
                          )}
                        </td>
                        <td className="num">{logic.fmtNum(t.total)}</td>
                        <td>{t.unit ?? '—'}</td>
                        <td className="num">{t.norms.length}</td>
                        <td className="num">{t.rows}</td>
                        <td>
                          {t.presence}
                          {t.spRows.length > 0 && (
                            <Small>оценка — у строк СП: {t.spRows.join(', ')}</Small>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            </Group>
          )}

          <Bar>
            <Toggle>
              <ToggleBtn $on={view === 'work'} onClick={() => setView('work')}>
                По видам работ
              </ToggleBtn>
              <ToggleBtn $on={view === 'system'} onClick={() => setView('system')}>
                По системам
              </ToggleBtn>
            </Toggle>
            <label style={{ fontSize: 13 }}>
              Статус{' '}
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as logic.FindingFilter['status'])}
              >
                <option value="all">все</option>
                <option value="proposed">предложено</option>
                <option value="accepted">принято</option>
                <option value="rejected">отклонено</option>
              </select>
            </label>
            {hiddenCount > 0 && (
              <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="checkbox"
                  checked={showHidden}
                  onChange={(e) => setShowHidden(e.target.checked)}
                />
                Показать скрытые — расходники и устаревшие ({hiddenCount})
              </label>
            )}
            {rowUid && (
              <Button $variant="ghost" $size="sm" onClick={onClearRow}>
                Только строка ВОР ✕
              </Button>
            )}
            <Muted>Находок: {filtered.length}</Muted>
          </Bar>

          {filtered.length === 0 ? (
            <Muted>Находок по выбранным условиям нет.</Muted>
          ) : view === 'work' ? (
            groups.map((g) => (
              <Group key={g.key}>
                <GroupHead onClick={() => setCollapsed((c) => ({ ...c, [g.key]: !c[g.key] }))}>
                  <span>
                    {collapsed[g.key] ? '▸' : '▾'} {g.label}
                    {g.key !== 'reconcile' && g.key !== g.label && (
                      <Small as="span"> · {g.key}</Small>
                    )}
                  </span>
                  <Small as="span">{g.items.length}</Small>
                </GroupHead>
                {!collapsed[g.key] &&
                  g.items.map((f) => (
                    <FindingCard
                      key={f.id}
                      f={f}
                      revisionId={revisionId}
                      docCode={docCode}
                      canWrite={canWrite}
                      canSaveRules={canSaveRules}
                      onOpenSheet={onOpenSheet}
                    />
                  ))}
              </Group>
            ))
          ) : (
            systems.map((s) => (
              <Group key={s.system}>
                <GroupHead
                  onClick={() => setCollapsed((c) => ({ ...c, [s.system]: !c[s.system] }))}
                >
                  <span>
                    {collapsed[s.system] ? '▸' : '▾'} {s.system}
                  </span>
                  <Small as="span">{s.items.length}</Small>
                </GroupHead>
                {!collapsed[s.system] &&
                  s.items.map((it) => (
                    <FindingCard
                      key={`${s.system}-${it.finding.id}`}
                      f={it.finding}
                      revisionId={revisionId}
                      docCode={docCode}
                      canWrite={canWrite}
                      canSaveRules={canSaveRules}
                      onOpenSheet={onOpenSheet}
                      estimate={logic.hasNormEstimate(it.finding) ? it.estimate : undefined}
                    />
                  ))}
              </Group>
            ))
          )}
        </>
      )}
    </Wrap>
  );
}
